import { SNAKE_SPEED_ORDER, type SnakeBests, type SnakeSpeed } from "@bing/shared";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError } from "../../api/client";
import { Button } from "../../components/Button";
import { SegmentedChoice } from "../../components/SegmentedChoice";
import { StartRow, StartScreenShell } from "../../components/StartScreenShell";
import { faDigits } from "../../i18n/digits";
import { fa } from "../../i18n/fa";
import { getSnakeLook, getSnakeSpeed, setSnakeLook, setSnakeSpeed } from "./prefs";
import { useSession, useSessionStore } from "../../store/context";
import { takeResumeIntent } from "../fresh-solo";
import { SnakeBestScore, type SnakeBestRefresh, type SnakeBestStorage } from "./best";
import type { SnakeLook } from "./draw";
import { SnakeTable } from "./SnakeTable";

/**
 * «مار» — THE GAME'S CONTAINER: the start screen (the chosen speed's best score, the speed, the look,
 * «شروع»), the play screen, and the refusal when the platform stops a NEW game.
 *
 * ⚠️ THE PLATFORM'S GATES. The other solo games meet them at the game-server's door (a NEW solo
 * game's join: ban → channel → maintenance → «بزودی»); Snake never opens a socket, so it meets the
 * same ladder at the api's — every authenticated route runs maintenance → ban → channel, and the
 * start screen's best-score read is one. Its refusal (and the api client's mirror of the other two,
 * `maintenance` / `channelGate`, which any later request keeps current) stops «شروع» and «دوباره»;
 * a game already running always finishes. A failure with no verdict — the network — stops nothing:
 * the game needs no server to play. «بزودی» has no switch for Snake (it has no `GameConfig` row).
 */

const SPEED_OPTIONS = SNAKE_SPEED_ORDER.map((s) => [s, fa.snake.speeds[s]] as const);
const LOOK_OPTIONS = (["modern", "retro"] as const).map((l) => [l, fa.snake.looks[l]] as const);

/** `localStorage`, or nothing at all where the WebView refuses it. */
function deviceStorage(): SnakeBestStorage {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
    // storage blocked (private mode)
  }
  const none: SnakeBestStorage = {
    getItem: () => null,
    setItem: () => {},
    removeItem: () => {},
  };
  return none;
}

/** The api's refusal of a NEW game — the platform's gates — or null for anything else. */
function gateRefusal(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code === "BANNED") return fa.errors.BANNED;
  if (error.code === "MAINTENANCE") return error.message || fa.errors.MAINTENANCE;
  if (error.code === "CHANNELS_REQUIRED") return fa.errors.CHANNELS_REQUIRED;
  return null;
}

export function SnakeGame(): ReactNode {
  const setView = useSession((s) => s.setView);
  const api = useSession((s) => s.api);
  const owner = useSession((s) => s.profile?.id ?? "anon");
  const session = useSessionStore();

  // ⚠️ EVERY SOLO GAME MUST CONSUME `takeResumeIntent()` — Snake has no saves, so it only clears the
  // flag (the lobby arms it for every solo resume; left set, the NEXT game would skip its picker).
  const [screen, setScreen] = useState<"start" | "play" | "refused">(() => {
    takeResumeIntent();
    return "start";
  });
  const [speed, setSpeed] = useState<SnakeSpeed>(getSnakeSpeed);
  const [look, setLook] = useState<SnakeLook>(getSnakeLook);
  const scores = useMemo(() => new SnakeBestScore(deviceStorage(), api, owner), [api, owner]);
  /** The best at every speed — the start screen shows the chosen one's, and another's the moment it
   *  is picked. */
  const [bests, setBests] = useState<SnakeBests>(() => scores.bests);
  /** The server's latest answer — a run that began before it arrived raises its own speed's best by
   *  it. */
  const [serverBests, setServerBests] = useState<SnakeBests | null>(null);
  /** The latest read was refused by a gate (a ban has no mirror in the session). */
  const refusedBy = useRef<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * A read's answer, WHENEVER it lands — a «شروع» tapped before the start screen's read answered is
   * already playing, and must still hear it. Its error is the gates' verdict on the next new game;
   * a failure with no verdict (the network) refuses nothing.
   */
  const heard = useCallback((answer: SnakeBestRefresh): void => {
    if (!mounted.current) return;
    setBests(answer.best);
    if (answer.server !== null) setServerBests(answer.server);
    refusedBy.current = gateRefusal(answer.error);
  }, []);

  // Each visit to the start screen: the cached bests at once, then the server's — and every pending
  // best retried, once.
  useEffect(() => {
    if (screen !== "start") return;
    setBests(scores.bests);
    void scores.refresh().then(heard);
  }, [screen, scores, heard]);

  /** A finished game: one read, so «دوباره» asks the gates' latest verdict. */
  const checkAfterGame = useCallback((): void => {
    void scores.check().then(heard);
  }, [scores, heard]);

  /** The gates' verdict on a NEW game right now: the refusal to show, or null to play. */
  const newGameRefusal = (): string | null => {
    if (refusedBy.current) return refusedBy.current;
    const { maintenance, channelGate } = session.getState();
    if (maintenance?.active) return maintenance.message || fa.errors.MAINTENANCE;
    // an empty list is no gate (the app's own reading: `shouldShowChannelGate`)
    if (channelGate && channelGate.length > 0) return fa.errors.CHANNELS_REQUIRED;
    return null;
  };

  const mayStart = (): boolean => {
    const refused = newGameRefusal();
    if (refused === null) return true;
    setRefusal(refused);
    setScreen("refused");
    return false;
  };

  const toLobby = (): void => setView("lobby");

  if (screen === "refused") {
    return (
      <div
        data-testid="snake-refused"
        style={{
          height: "var(--app-h)",
          display: "grid",
          placeItems: "center",
          background: "var(--room-bg)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <p style={{ color: "var(--danger)", margin: 0, fontWeight: 700 }}>{refusal}</p>
          <Button variant="primary" onClick={toLobby}>
            {fa.snake.leave}
          </Button>
        </div>
      </div>
    );
  }

  if (screen === "play") {
    return (
      <SnakeTable
        speed={speed}
        look={look}
        scores={scores}
        serverBest={serverBests?.[speed] ?? null}
        onGameOver={checkAfterGame}
        mayStartAgain={mayStart}
        onBack={() => setScreen("start")}
        onExit={toLobby}
      />
    );
  }

  return (
    <StartScreenShell
      title={fa.snake.title}
      emoji="🐍"
      heading={fa.soloStart.heading}
      leaveLabel={fa.snake.leave}
      startLabel={fa.snake.start}
      onStart={() => {
        if (mayStart()) setScreen("play");
      }}
      onLeave={toLobby}
    >
      <StartRow label={fa.snake.best}>
        <div
          data-testid="snake-start-best"
          className="font-headline-md text-headline-md font-bold tabular-nums text-secondary"
        >
          {faDigits(bests[speed])}
        </div>
      </StartRow>
      <StartRow label={fa.snake.speed}>
        <SegmentedChoice
          label={fa.snake.speed}
          options={SPEED_OPTIONS}
          value={speed}
          onChange={(value) => {
            setSpeed(value);
            setSnakeSpeed(value);
          }}
          compact
        />
      </StartRow>
      <StartRow label={fa.snake.look}>
        <SegmentedChoice
          label={fa.snake.look}
          options={LOOK_OPTIONS}
          value={look}
          onChange={(value) => {
            setLook(value);
            setSnakeLook(value);
          }}
          compact
        />
      </StartRow>
    </StartScreenShell>
  );
}
